import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Loader2, Save, Eye, FlaskConical, Send, ShieldCheck, CalendarClock } from "lucide-react";
import { EmailPreviewFrame } from "./ComposeAnnouncement";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ORDER = ["welcome", "join_received", "weekly_reminder", "recording_published"];
const hourLabel = (h) => `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`;

function AutomationCard({ id, cfg, onChange, children }) {
  return (
    <Card className="border-border bg-card" data-testid={`automation-${id}`}>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-foreground">{cfg.label}</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">{cfg.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground" data-testid={`automation-${id}-state`}>{cfg.enabled ? "On" : "Off"}</span>
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => onChange({ enabled: v })} data-testid={`automation-${id}-toggle`} />
          </div>
        </div>
        <div className="grid gap-3">
          <div className="space-y-1.5"><Label htmlFor={`${id}-subject`}>Subject</Label><Input id={`${id}-subject`} value={cfg.subject} onChange={(e) => onChange({ subject: e.target.value })} data-testid={`automation-${id}-subject`} /></div>
          <div className="space-y-1.5"><Label htmlFor={`${id}-intro`}>Opening line</Label><Textarea id={`${id}-intro`} rows={3} value={cfg.intro} onChange={(e) => onChange({ intro: e.target.value })} data-testid={`automation-${id}-intro`} /></div>
          <p className="text-xs text-muted-foreground">Placeholders: {cfg.placeholders.map((p) => <code key={p} className="mr-1 rounded bg-secondary px-1">{`{${p}}`}</code>)}</p>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function ReminderExtras({ cfg, onChange }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/email/reminder/preview").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "test") toast.success((await api.post("/admin/email/reminder/test")).data.message);
      else { const r = (await api.post("/admin/email/reminder/send-now")).data; toast.success(`Reminder going to ${r.recipients_count} members`); load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Send on</Label>
          <Select value={String(cfg.send_day)} onValueChange={(v) => onChange({ send_day: Number(v) })}>
            <SelectTrigger data-testid="reminder-day-select"><SelectValue /></SelectTrigger>
            <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>At (Pacific)</Label>
          <Select value={String(cfg.send_hour)} onValueChange={(v) => onChange({ send_hour: Number(v) })}>
            <SelectTrigger data-testid="reminder-hour-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="rounded-lg border border-border bg-secondary/30 p-3 text-sm" data-testid="reminder-next-info">
        <p className="flex items-center gap-1.5 font-medium text-foreground"><CalendarClock className="h-4 w-4 text-primary" />Next up</p>
        {info?.event ? (
          <p className="mt-1 text-muted-foreground">{info.event.title} · {info.event.theme} at {info.event.venue} · {info.event.time}. Goes to {info.count} members{info.already_sent_at ? ` — already sent ${new Date(info.already_sent_at).toLocaleDateString()}` : ""}.</p>
        ) : <p className="mt-1 text-muted-foreground">No published event in the next 7 days — nothing will send until one exists.</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" disabled={!info?.event || !!busy} onClick={() => setInfo((i) => ({ ...i, show: !i.show }))} data-testid="reminder-preview-button"><Eye className="mr-2 h-4 w-4" />{info?.show ? "Hide preview" : "Preview"}</Button>
        <Button variant="outline" size="sm" disabled={!info?.event || !!busy} onClick={() => act("test")} data-testid="reminder-test-button">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FlaskConical className="mr-2 h-4 w-4" />Send me a test</>}</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button size="sm" disabled={!info?.event || !!busy} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="reminder-send-now-button"><Send className="mr-2 h-4 w-4" />Send reminder now</Button></AlertDialogTrigger>
          <AlertDialogContent data-testid="reminder-confirm-dialog">
            <AlertDialogHeader><AlertDialogTitle>Send this week’s reminder now?</AlertDialogTitle><AlertDialogDescription>Goes to {info?.count} members right away{info?.already_sent_at ? " — they already received one for this Friday" : ""}.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => act("send")} className="bg-primary text-primary-foreground" data-testid="reminder-confirm-send">Send now</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {info?.show && info?.html && <EmailPreviewFrame html={info.html} testid="reminder-preview-frame" />}
    </div>
  );
}

export default function AutomationsPanel() {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/email/settings").then((r) => { setCfg(r.data.automations); setMeta(r.data); }).catch(() => toast.error("Couldn't load email settings"));
  }, []);

  const patch = (id) => (fields) => { setCfg((c) => ({ ...c, [id]: { ...c[id], ...fields } })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { setCfg((await api.put("/admin/email/settings", { automations: cfg })).data.automations); setDirty(false); toast.success("Email settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="automations-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">Sent from <span className="font-medium text-foreground" data-testid="email-sender">{meta.sender}</span>. Flip a switch to stop an email entirely, or reword it — changes apply to the next send.</p>
        <Button onClick={save} disabled={!dirty || saving} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="automations-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" />{dirty ? "Save changes" : "Saved"}</>}</Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {ORDER.map((id) => (
          <AutomationCard key={id} id={id} cfg={cfg[id]} onChange={patch(id)}>
            {id === "weekly_reminder" && <ReminderExtras cfg={cfg[id]} onChange={patch(id)} />}
            {id === "recording_published" && <p className="border-t border-border pt-3 text-xs text-muted-foreground">Fires when a <span className="font-medium">public</span> video finishes processing. You can also send one by hand from Studio → Videos → “Email members”.</p>}
          </AutomationCard>
        ))}
      </div>
      <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/30 p-4 text-sm text-muted-foreground" data-testid="security-emails-note">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p><span className="font-medium text-foreground">Always on:</span> password reset, set-your-password invites and temporary passwords. These are account-security emails a member (or you) explicitly asks for, so they can’t be switched off and ignore opt-outs.</p>
      </div>
    </div>
  );
}
